// Normaliza qualquer formato de data que circula no app — Timestamp do
// Firestore, `{seconds, nanoseconds}` (o que sobra quando o doc passa por JSON),
// Date, número em segundos ou milissegundos, string ISO — para um Date.
//
// Devolve null quando não dá para interpretar, em vez de deixar o `.toDate()`
// estourar: um único comentário legado gravado por script derrubava a linha
// inteira da lista com TypeError.
export const toDateSafe = (value) => {
    if (!value) return null;

    if (value instanceof Date) {
        return isNaN(value.getTime()) ? null : value;
    }

    if (typeof value?.toDate === 'function') {
        const date = value.toDate();
        return isNaN(date.getTime()) ? null : date;
    }

    if (typeof value?.seconds === 'number') {
        return new Date(value.seconds * 1000 + (value.nanoseconds || 0) / 1e6);
    }

    if (typeof value === 'number') {
        // Abaixo de 1e12 só pode ser segundos (1e12 ms = 2001).
        return new Date(value < 1e12 ? value * 1000 : value);
    }

    if (typeof value === 'string') {
        const parsed = Date.parse(value);
        return Number.isNaN(parsed) ? null : new Date(parsed);
    }

    return null;
};

// "há X minutos" em pt-BR, para comentários, respostas e chat ao vivo.
//
// `now` é injetável só para os testes; no app é sempre o relógio do leitor.
export const timeAgo = (value, now = new Date()) => {
    const date = toDateSafe(value);
    if (!date) return null;

    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    // Cobre também o relógio do leitor atrasado em relação ao servidor: sem
    // isto o cálculo devolvia "há -4 segundos" logo depois de comentar.
    if (seconds < 10) return 'agora';

    const units = [
        { seconds: 31536000, singular: 'ano', plural: 'anos' },
        { seconds: 2592000, singular: 'mês', plural: 'meses' },
        { seconds: 86400, singular: 'dia', plural: 'dias' },
        { seconds: 3600, singular: 'hora', plural: 'horas' },
        { seconds: 60, singular: 'minuto', plural: 'minutos' },
    ];

    for (const unit of units) {
        const interval = Math.floor(seconds / unit.seconds);
        // `>= 1`, não `> 1`: com o limiar antigo "há 1 dia" nunca aparecia —
        // caía para a unidade de baixo e virava "há 25 horas". Idem 1 mês, 1 ano.
        if (interval >= 1) {
            return `há ${interval} ${interval === 1 ? unit.singular : unit.plural}`;
        }
    }

    return `há ${seconds} segundos`;
};

export const formatDateBR = (date) => {
    if (!date) return '-';

    // Se a data já estiver no formato "DD/MM/YYYY", retorna-a
    if (typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) return date;

    let dateObj = null;
    if (date instanceof Date) {
        dateObj = date;
    } else if (typeof date?.toDate === 'function') {
        dateObj = date.toDate();
    } else if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
        // "AAAA-MM-DD" puro (sem hora, ex.: <input type="date">): o motor JS
        // interpreta como meia-noite UTC. Em fusos negativos (Brasil, UTC-3) a
        // conversão para local volta um dia (23/02 em vez de 24/02). Construído
        // como data de calendário local, não como instante UTC.
        const [year, month, day] = date.split('-').map(Number);
        dateObj = new Date(year, month - 1, day);
    } else {
        dateObj = new Date(date);
    }

    return !isNaN(dateObj.getTime())
        ? dateObj.toLocaleDateString('pt-BR', { year: 'numeric', month: '2-digit', day: '2-digit' })
        : '-';
};